import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { Play, Pause, Square, Trash2 } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { derivePayLevel, TimeEntryModal } from './TimeEntryShared'

// ── Timer model ───────────────────────────────────────────────────────────────
// An in-flight timer is a time_entries row with started_at set and ended_at null.
// This covers two cases:
//  - Stopwatch: `duration_seconds` holds accumulated ACTIVE time; `running_since`
//    is the moment the current running segment began (null while paused). Live
//    elapsed = duration_seconds + (running_since ? now - running_since : 0). On
//    pause/stop we fold the running segment into duration_seconds.
//  - Manual draft (is_manual_draft = true): created from the "Log time" modal
//    with just a start time, no running_since, no accumulated duration yet. Live
//    elapsed = now - started_at. Finishing one means typing an end time (or
//    duration) in the same modal, which sets ended_at/duration_seconds directly.
// Either way, the DB partial unique index guarantees at most one in-flight row
// per user — starting a stopwatch blocks opening a draft and vice versa.

const TimerContext = createContext(null)
export const useTimer = () => useContext(TimerContext)

// A timer running longer than this is almost certainly forgotten (a normal task
// rarely exceeds an hour). We only REMIND — never auto-pause. Change this one number
// to adjust how long before the reminder appears.
const RUNAWAY_SECONDS = 2 * 60 * 60 // 2 hours

const nowIso = () => new Date().toISOString()
const todayStr = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function clock(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(sec).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`
}

export function TimerProvider({ currentUser, profile, allClients, tasks, children }) {
  const toast = useToast()
  const [activeEntry, setActiveEntry] = useState(null)
  const [now, setNow] = useState(Date.now())
  const tickRef = useRef(null)

  const loadActive = useCallback(async () => {
    if (!currentUser?.id) return
    const { data, error } = await supabase
      .from('time_entries')
      .select('*')
      .eq('user_id', currentUser.id)
      .is('ended_at', null)
      .not('started_at', 'is', null)
      .order('started_at', { ascending: false })
      .limit(1)
    if (error) return
    setActiveEntry(data && data.length ? data[0] : null)
  }, [currentUser?.id])

  useEffect(() => { loadActive() }, [loadActive])

  // Tick once a second while actively running OR while a manual draft is open
  // (paused stopwatch = frozen, no interval needed).
  useEffect(() => {
    if (activeEntry && (activeEntry.running_since || activeEntry.is_manual_draft)) {
      tickRef.current = setInterval(() => setNow(Date.now()), 1000)
      return () => clearInterval(tickRef.current)
    }
  }, [activeEntry])

  let elapsedSeconds = 0
  if (activeEntry) {
    if (activeEntry.is_manual_draft) {
      elapsedSeconds = Math.max(0, (now - Date.parse(activeEntry.started_at)) / 1000)
    } else {
      elapsedSeconds = activeEntry.duration_seconds || 0
      if (activeEntry.running_since) {
        elapsedSeconds += Math.max(0, (now - Date.parse(activeEntry.running_since)) / 1000)
      }
    }
  }

  let activeLabel = ''
  if (activeEntry) {
    const t = activeEntry.task_id ? (tasks || []).find(x => x.id === activeEntry.task_id) : null
    if (t) activeLabel = t.title
    else {
      const c = activeEntry.client_id ? (allClients || []).find(x => x.id === activeEntry.client_id) : null
      activeLabel = c ? c.name : 'Time entry'
    }
  }

  const start = async (task) => {
    if (activeEntry) {
      toast(activeEntry.is_manual_draft ? 'Finish your draft time entry first' : 'Stop your current timer first', 'error')
      return
    }
    if (!profile?.role) { toast("Your profile role hasn't loaded — refresh and try again", 'error'); return }
    const client = task?.client_id ? (allClients || []).find(c => c.id === task.client_id) : null
    const payload = {
      user_id: currentUser.id,
      client_id: task?.client_id || null,
      task_id: task?.id || null,
      project_id: task?.project_id || null,
      service_area: task?.service_area || null,
      category: 'task',
      pay_level: derivePayLevel(currentUser.id, profile.role, client),
      entry_date: todayStr(),
      started_at: nowIso(),
      running_since: nowIso(),
      duration_seconds: 0,
      ended_at: null,
    }
    const { data, error } = await supabase.from('time_entries').insert(payload).select().single()
    if (error) { toast('Could not start timer: ' + error.message, 'error'); return }
    setActiveEntry(data)
  }

  const pause = async () => {
    if (!activeEntry?.running_since) return
    const add = Math.max(0, Math.round((Date.now() - Date.parse(activeEntry.running_since)) / 1000))
    const newDur = (activeEntry.duration_seconds || 0) + add
    const { data, error } = await supabase.from('time_entries')
      .update({ duration_seconds: newDur, running_since: null }).eq('id', activeEntry.id).select().single()
    if (error) { toast('Could not pause: ' + error.message, 'error'); return }
    setActiveEntry(data)
  }

  const resume = async () => {
    if (!activeEntry || activeEntry.running_since) return
    const { data, error } = await supabase.from('time_entries')
      .update({ running_since: nowIso() }).eq('id', activeEntry.id).select().single()
    if (error) { toast('Could not resume: ' + error.message, 'error'); return }
    setActiveEntry(data)
  }

  // Stop/finish the active entry, finalizing at `endMs` (defaults to now). Used by
  // the normal Stop button, the runaway reminder, and (for drafts) the quick
  // "finish now" action on the timer bar / task control.
  const stopAt = async (endMs = Date.now()) => {
    if (!activeEntry) return

    if (activeEntry.is_manual_draft) {
      const duration = Math.round((endMs - Date.parse(activeEntry.started_at)) / 1000)
      if (duration <= 0) { toast("End time can't be before the draft's start time", 'error'); return }
      const { error } = await supabase.from('time_entries')
        .update({ duration_seconds: duration, ended_at: new Date(endMs).toISOString() }).eq('id', activeEntry.id)
      if (error) { toast('Could not finish draft: ' + error.message, 'error'); return }
      toast('Draft finished — ' + clock(duration) + ' logged', 'success')
      setActiveEntry(null)
      return
    }

    let newDur = activeEntry.duration_seconds || 0
    if (activeEntry.running_since) {
      newDur += Math.max(0, Math.round((endMs - Date.parse(activeEntry.running_since)) / 1000))
    }
    if (newDur <= 0) {
      // Nothing was logged — discard the empty entry rather than clutter the log.
      const { error } = await supabase.from('time_entries').delete().eq('id', activeEntry.id)
      if (error) { toast('Could not stop timer: ' + error.message, 'error'); return }
      setActiveEntry(null)
      return
    }
    const { error } = await supabase.from('time_entries')
      .update({ duration_seconds: newDur, running_since: null, ended_at: new Date(endMs).toISOString() }).eq('id', activeEntry.id)
    if (error) { toast('Could not stop timer: ' + error.message, 'error'); return }
    toast('Timer stopped — ' + clock(newDur) + ' logged', 'success')
    setActiveEntry(null)
  }
  const stop = () => stopAt(Date.now())

  // Abandon an open draft entirely (no time logged). Only valid for drafts — a
  // stopwatch session with real accumulated time should go through Stop instead.
  const discardDraft = async () => {
    if (!activeEntry || !activeEntry.is_manual_draft) return
    const { error } = await supabase.from('time_entries').delete().eq('id', activeEntry.id)
    if (error) { toast('Could not discard draft: ' + error.message, 'error'); return }
    toast('Draft discarded', 'success')
    setActiveEntry(null)
  }

  const value = {
    activeEntry, activeLabel, elapsedSeconds, start, pause, resume, stop, stopAt, discardDraft,
    refresh: loadActive, currentUser, profile, allClients, tasks,
  }
  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>
}

// ── Global running-timer bar (visible on every view) ──────────────────────────
export function ActiveTimerBar() {
  const ctx = useTimer()
  const [showFinish, setShowFinish] = useState(false)
  if (!ctx || !ctx.activeEntry) return null
  const { activeEntry, activeLabel, elapsedSeconds, pause, resume, stop, discardDraft } = ctx
  const isDraft = !!activeEntry.is_manual_draft
  const running = !isDraft && !!activeEntry.running_since

  const discard = () => {
    if (!window.confirm('Discard this draft? No time will be logged.')) return
    discardDraft()
  }

  return (
    <>
      <div style={{
        position: 'fixed', top: 14, left: '50%', transform: 'translateX(-50%)', zIndex: 2000,
        background: 'var(--navy)', color: '#fff', borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow-lg)', padding: '10px 14px',
        display: 'flex', alignItems: 'center', gap: 14, maxWidth: 360,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: 10, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {isDraft ? 'Draft in progress' : (running ? 'Tracking' : 'Paused')}
          </span>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 18, fontWeight: 700, lineHeight: 1.1 }}>{clock(elapsedSeconds)}</span>
          {activeLabel && (
            <span style={{ fontSize: 12, opacity: 0.85, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>{activeLabel}</span>
          )}
        </div>
        {isDraft ? (
          <>
            <button className="btn-icon" title="Finish" onClick={() => setShowFinish(true)} style={{ color: '#fff' }}><Square size={16} /></button>
            <button className="btn-icon" title="Discard draft" onClick={discard} style={{ color: '#fff' }}><Trash2 size={16} /></button>
          </>
        ) : (
          <>
            {running
              ? <button className="btn-icon" title="Pause" onClick={pause} style={{ color: '#fff' }}><Pause size={16} /></button>
              : <button className="btn-icon" title="Resume" onClick={resume} style={{ color: '#fff' }}><Play size={16} /></button>}
            <button className="btn-icon" title="Stop & log" onClick={stop} style={{ color: '#fff' }}><Square size={16} /></button>
          </>
        )}
      </div>
      {showFinish && (
        <TimeEntryModal
          allClients={ctx.allClients}
          tasks={ctx.tasks}
          currentUser={ctx.currentUser}
          profile={ctx.profile}
          editing={activeEntry}
          activeElsewhere={null}
          onClose={() => setShowFinish(false)}
          onSaved={() => { setShowFinish(false); ctx.refresh() }}
        />
      )}
    </>
  )
}

// ── Per-task control (rendered inside TaskDetail) ─────────────────────────────
export function TaskTimerButton({ task }) {
  const ctx = useTimer()
  const [showFinish, setShowFinish] = useState(false)
  if (!task) return null
  if (!ctx) {
    return <span style={{ fontSize: 11, color: 'var(--red)', fontWeight: 600 }}>⚠ timer unavailable (no context)</span>
  }
  const { activeEntry, elapsedSeconds, start, pause, resume, stop, discardDraft } = ctx
  const isThis = activeEntry && activeEntry.task_id === task.id
  const isDraft = !!activeEntry?.is_manual_draft
  const running = isThis && !isDraft && !!activeEntry.running_since

  const finishModal = showFinish && (
    <TimeEntryModal
      allClients={ctx.allClients}
      tasks={ctx.tasks}
      currentUser={ctx.currentUser}
      profile={ctx.profile}
      editing={activeEntry}
      activeElsewhere={null}
      onClose={() => setShowFinish(false)}
      onSaved={() => { setShowFinish(false); ctx.refresh() }}
    />
  )

  if (!activeEntry) {
    return (
      <button className="btn btn-sm" style={{ background: 'var(--teal)', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => start(task)}>
        <Play size={14} /> Start timer
      </button>
    )
  }

  if (!isThis) {
    return (
      <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {isDraft
          ? 'You have an open draft time entry — finish it before starting a timer here.'
          : 'A timer is running on another task — stop it first to track this one.'}
        {isDraft && <button className="btn btn-sm btn-ghost" onClick={() => setShowFinish(true)}>Finish draft</button>}
        {finishModal}
      </span>
    )
  }

  if (isDraft) {
    const discard = () => {
      if (!window.confirm('Discard this draft? No time will be logged.')) return
      discardDraft()
    }
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--navy)' }}>{clock(elapsedSeconds)}</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>draft open</span>
        <button className="btn btn-sm btn-gold" onClick={() => setShowFinish(true)}>Finish</button>
        <button className="btn btn-sm btn-ghost" onClick={discard}>Discard</button>
        {finishModal}
      </div>
    )
  }

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--navy)' }}>{clock(elapsedSeconds)}</span>
      {running
        ? <button className="btn btn-sm btn-ghost" onClick={pause} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Pause size={14} /> Pause</button>
        : <button className="btn btn-sm" onClick={resume} style={{ background: 'var(--teal)', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6 }}><Play size={14} /> Resume</button>}
      <button className="btn btn-sm btn-danger" onClick={stop} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Square size={14} /> Stop</button>
    </div>
  )
}

// ── Runaway-timer reminder ────────────────────────────────────────────────────
// Reminds (never forces) when the active timer has been running an implausibly
// long time — the forgotten-timer case. Surfaces when the user is looking at the
// app and when they return to the tab, since work mostly happens in other apps.
// They can confirm it's accurate or correct the stop time. No auto-pause, so an
// occasional genuinely-long task is never interrupted.
function toLocalDatetime(d) {
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function RunawayTimerReminder() {
  const ctx = useTimer()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [dismissedId, setDismissedId] = useState(null)
  const [stopInput, setStopInput] = useState('')
  const dismissedRef = useRef(null)
  useEffect(() => { dismissedRef.current = dismissedId }, [dismissedId])

  const entry = ctx?.activeEntry || null
  const entryId = entry?.id || null
  const elapsed = ctx?.elapsedSeconds || 0
  const longRunning = !!entry && elapsed > RUNAWAY_SECONDS

  useEffect(() => {
    if (!entryId) { setOpen(false); return }
    const check = () => {
      if (longRunning && dismissedRef.current !== entryId && document.visibilityState !== 'hidden') {
        setStopInput(toLocalDatetime(new Date()))
        setOpen(true)
      }
    }
    check() // on mount/reload and whenever the timer crosses the threshold
    const onFocus = () => check()
    const onVis = () => { if (document.visibilityState === 'visible') check() }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVis)
    return () => { window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onVis) }
  }, [entryId, longRunning])

  if (!open || !ctx || !entry) return null
  const isDraft = !!entry.is_manual_draft

  const keepRunning = () => { setDismissedId(entryId); setOpen(false) }
  const stopAtChosen = () => {
    const chosen = new Date(stopInput)
    if (isNaN(chosen.getTime())) { toast('Enter a valid stop time', 'error'); return }
    if (chosen.getTime() < Date.parse(entry.started_at)) { toast("Stop time can't be before the timer started", 'error'); return }
    if (chosen.getTime() > Date.now() + 60000) { toast("Stop time can't be in the future", 'error'); return }
    ctx.stopAt(chosen.getTime())
    setOpen(false)
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <div className="modal-title">{isDraft ? 'Draft still open' : 'Timer still running'}</div>
        </div>
        <div className="modal-body">
          <p style={{ marginTop: 0 }}>
            Your {isDraft ? 'draft time entry' : 'timer'}{ctx.activeLabel ? ` for \u201C${ctx.activeLabel}\u201D` : ''} has been{' '}
            {isDraft ? 'open' : 'running'} for <strong>{clock(elapsed)}</strong>, since {new Date(entry.started_at).toLocaleString()}.
            Is that accurate?
          </p>
          <div className="form-group">
            <label className="form-label">If not, set when you actually {isDraft ? 'finished' : 'stopped'}:</label>
            <input className="form-control" type="datetime-local" value={stopInput} onChange={e => setStopInput(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={keepRunning}>Yes, keep it open</button>
          <button className="btn btn-primary" onClick={stopAtChosen}>{isDraft ? 'Finish at this time' : 'Stop at this time'}</button>
        </div>
      </div>
    </div>
  )
}